import { ReactNode } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { focusRingSx, selectedSurfaceSx } from 'components/ui/styleKit';
import { pop, useTourMotion } from 'sections/welcome-tour/motion';

interface CheckCardProps {
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/** A whole-row checkbox: the animated check, then the caller's content; checked rows take the shared selected surface. */
const CheckCard = ({ checked, disabled, onToggle, children }: CheckCardProps) => {
  const theme = useTheme();
  const { v } = useTourMotion();
  const primary = theme.palette.primary.main;

  return (
    <ButtonBase
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onToggle}
      sx={{
        ...focusRingSx(theme),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        textAlign: 'left',
        borderRadius: RADIUS.control,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'border-color .2s ease, background-color .2s ease, box-shadow .25s ease, opacity .2s ease',
        '&:hover': { borderColor: alpha(primary, 0.5), backgroundColor: alpha(primary, 0.03) },
        '&.Mui-disabled': { opacity: 0.45 },
        ...(checked ? selectedSurfaceSx(theme) : {}),
        [REDUCED_MOTION]: { transition: 'none' }
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: '7px',
          border: `1.5px solid ${checked ? primary : theme.palette.divider}`,
          transition: 'border-color .2s ease',
          [REDUCED_MOTION]: { transition: 'none' }
        }}
      >
        <AnimatePresence initial={false}>
          {checked && (
            <Box
              component={motion.div}
              variants={v(pop)}
              initial="hidden"
              animate="show"
              exit="hidden"
              sx={{
                position: 'absolute',
                inset: -1.5,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: theme.palette.primary.contrastText,
                backgroundColor: primary,
                boxShadow: `0 4px 12px -4px ${alpha(primary, 0.8)}`
              }}
            >
              <CheckOutlined />
            </Box>
          )}
        </AnimatePresence>
      </Box>
      {children}
    </ButtonBase>
  );
};

export default CheckCard;
